import { memo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

interface MoveOrderDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'single' | 'bulk';
  selectedCount: number;
  date: Date;
  onDateChange: (date: Date) => void;
  isProcessing: boolean;
  onConfirm: () => void;
}

/**
 * 주문 날짜 변경/일괄 변경 다이얼로그 (presentational).
 * open state·moveOrderDate·확정 로직(onConfirm)은 Home이 소유.
 * onOpenChange는 Home이 처리중 가드(`if(!open && isBatchProcessing) return`)를 계산해 주입(raw setter 비노출).
 * 취소 버튼은 isProcessing일 때 disabled이므로 onOpenChange(false)로 닫아도 원본
 * `setIsMoveOrderDateDialogOpen(false)`와 동작 동일.
 */
export const MoveOrderDateDialog = memo(function MoveOrderDateDialog({
  open,
  onOpenChange,
  mode,
  selectedCount,
  date,
  onDateChange,
  isProcessing,
  onConfirm,
}: MoveOrderDateDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl px-8">
        <DialogHeader>
          <DialogTitle>
            {mode === 'bulk' ? '주문 날짜 일괄 변경' : '주문 날짜 변경'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'bulk'
              ? <>선택한 <span className="font-semibold text-indigo-700">{selectedCount}건</span>의 주문 날짜를 한 번에 변경합니다.</>
              : '현재 선택된 주문의 날짜를 변경합니다.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-6">
            <div className="border rounded-lg p-2 shadow-sm">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => {
                  if (d) {
                    onDateChange(d);
                  }
                }}
                initialFocus
              />
            </div>
            <div className="flex flex-col gap-3 min-w-[200px]">
              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">선택 날짜</div>
                <div className="text-base font-semibold text-gray-800 bg-white border border-gray-200 rounded-md px-3 py-2 shadow-sm tracking-tight w-[180px]">
                  {format(date, "yyyy년 MM월 dd일")}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">빠른 이동</div>
                <Button
                  variant="secondary"
                  className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  onClick={() => onDateChange(new Date())}
                >
                  오늘
                </Button>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {mode === 'bulk'
              ? `선택한 날짜의 정산서로 ${selectedCount}건이 함께 이동됩니다.`
              : '선택한 날짜로 이동 시, 해당 날짜의 정산서 목록에서 이 주문을 확인할 수 있습니다.'}
          </p>
        </div>
        <DialogFooter className="pr-2">
          <Button
            variant="ghost"
            className="text-gray-600 hover:bg-gray-100"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            취소
          </Button>
          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3"
            onClick={onConfirm}
            disabled={isProcessing}
          >
            {mode === 'bulk'
              ? (isProcessing ? '이동 중…' : `${selectedCount}건 이 날짜로 이동`)
              : '이 날짜로 이동'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
